import React, { useEffect, useRef } from 'react';
import { Plus, Trash2 } from 'lucide-react';

let keySeed = 0;
const newRowKey = () => {
  keySeed += 1;
  return `kv-${Date.now().toString(36)}-${keySeed}`;
};

/** { Difficulty: 'Beginner' } -> [{ _key, key: 'Difficulty', value: 'Beginner' }] */
export const objectToRows = obj =>
  Object.entries(obj || {}).map(([key, value]) => ({ _key: newRowKey(), key, value: value == null ? '' : String(value) }));

/** Rows back to an object; rows without a name are skipped. */
export const rowsToObject = rows =>
  (Array.isArray(rows) ? rows : []).reduce((acc, row) => {
    const key = (row?.key || '').trim();
    if (key) acc[key] = (row.value || '').trim();
    return acc;
  }, {});

/**
 * Editable list of name/value pairs.
 * value: [{ _key, key, value }]
 */
const KeyValueEditor = ({ value, onChange, id, keyPlaceholder = 'Name', valuePlaceholder = 'Value', addLabel = 'Add detail' }) => {
  const rows = Array.isArray(value) ? value : [];
  const focusKey = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!focusKey.current || !wrapRef.current) return;
    const input = wrapRef.current.querySelector(`[data-kv-key="${focusKey.current}"]`);
    focusKey.current = null;
    input?.focus();
  });

  const update = (index, patch) => onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const remove = index => onChange(rows.filter((_, i) => i !== index));
  const add = () => {
    const key = newRowKey();
    focusKey.current = key;
    onChange([...rows, { _key: key, key: '', value: '' }]);
  };

  return (
    <div id={id} tabIndex={-1} ref={wrapRef}>
      {rows.length > 0 && (
        <div className="adm-kv">
          {rows.map((row, index) => (
            <div className="adm-kv__row" key={row._key || index}>
              <input
                className="pk-input"
                data-kv-key={row._key}
                aria-label={`Detail ${index + 1} name`}
                placeholder={keyPlaceholder}
                value={row.key || ''}
                maxLength={60}
                onChange={e => update(index, { key: e.target.value })}
              />
              <input
                className="pk-input"
                aria-label={`Detail ${index + 1} value`}
                placeholder={valuePlaceholder}
                value={row.value || ''}
                maxLength={300}
                onChange={e => update(index, { value: e.target.value })}
              />
              <button
                type="button"
                className="adm-icon-btn"
                data-tone="danger"
                aria-label={`Remove detail ${index + 1}`}
                title="Remove"
                onClick={() => remove(index)}
              >
                <Trash2 aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}
      <button type="button" className="pk-btn pk-btn--secondary pk-btn--sm" onClick={add}>
        <Plus aria-hidden="true" />
        {addLabel}
      </button>
    </div>
  );
};

/** Form rule: names are required for filled-in values and must be unique. */
export const keyValueRule = {
  validator: (_, value) => {
    const rows = Array.isArray(value) ? value : [];
    const seen = new Set();
    for (let i = 0; i < rows.length; i += 1) {
      const key = (rows[i]?.key || '').trim();
      const val = (rows[i]?.value || '').trim();
      if (!key && val) return Promise.reject(new Error(`Detail ${i + 1} needs a name.`));
      if (key) {
        const lower = key.toLowerCase();
        if (seen.has(lower)) return Promise.reject(new Error(`"${key}" is listed twice.`));
        seen.add(lower);
      }
    }
    return Promise.resolve();
  },
};

export default KeyValueEditor;
